/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import {
  get,
  patch,
  deleteApi,
  checkResponse,
  checkHttpStatus,
  checkResponseGet,
  checkResponseGetMany,
} from "./httpFunctions";
import { AxiosResponse } from "axios";
import { intl } from "../containers/app";
import { handleError } from "./errorLogger";
import { error, success } from "./messages";

/**
 * odešle HTTP PATCH na API
 * @param entity název entity
 * @param entityId ID entity
 * @param properties vlastnosti entity k úpravě
 * @param what český název v 1. pádě
 * @param whatError český název ve 4. pádě
 * @param setLoading funkce pro nastavení načítání
 * @param loadEntities funkce pro načtení seznamu entit
 * @param she byla nebo byl smazána
 */
export function patchEntity(
  entity: string,
  entityId: number,
  properties: any,
  what?: string,
  whatError?: string,
  setLoading?: any,
  loadEntities?: any,
  she = false
): void {
  setLoading && setLoading(true);
  const query = `${entity}/${entityId}`;
  patch(query, createPatchModel(properties))
    .then((r) => {
      if (checkHttpStatus(r) && checkResponse(r)) {
        what &&
          success(
            r.data.message ||
              intl.formatMessage(
                {
                  id: "crud.edit",
                  defaultMessage: "{what} byl{suffix} úspěšně upraven{suffix}.",
                },
                { what, suffix: she ? "a" : "" }
              ),
            10
          );
        loadEntities && loadEntities();
      } else {
        whatError &&
          error(
            (r.data && r.data.message) ||
              intl.formatMessage(
                {
                  id: "crud.editError",
                  defaultMessage: "Při úpravě {whatError} nastala chyba!",
                },
                { whatError }
              ),
            10
          );
      }
      setLoading && setLoading(false);
    })
    .catch((err) => {
      setLoading && setLoading(false);
      whatError &&
        error(
          intl.formatMessage(
            {
              id: "crud.editError",
              defaultMessage: "Při úpravě {whatError} nastala chyba!",
            },
            { whatError }
          ),
          10
        );
      handleError(err);
    });
}

/**
 * odešle HTTP DELETE na API
 * @param entity název entity
 * @param entityId ID entity
 * @param what český název v 1. pádě
 * @param whatError český název ve 4. pádě
 * @param loadEntities funkce pro načtení seznamu entit
 * @param she byla nebo byl smazána
 */
export function deleteEntity(
  entity: string,
  entityId: number,
  what: string,
  whatError: string,
  loadEntities: any,
  she = false,
  suffix = "",
  message = ""
): void {
  const query = `${entity}${entityId ? "/" + entityId : ""}`;
  deleteApi(query)
    .then((r) => {
      if (checkHttpStatus(r) && checkResponse(r)) {
        success(
          message ||
            intl.formatMessage(
              {
                id: "crud.delete",
                defaultMessage: "{what} byl{suffix} úspěšně smazán{suffix}.",
              },
              { what, suffix: suffix || (she ? "a" : "") }
            )
        );
        loadEntities();
      } else {
        error(
          (r.data && r.data.message) ||
            intl.formatMessage(
              {
                id: "crud.deleteError",
                defaultMessage: "Při mazání {whatError} nastala chyba!",
              },
              { whatError }
            ),
          10
        );
      }
    })
    .catch((err) => {
      error(
        intl.formatMessage(
          {
            id: "crud.deleteError",
            defaultMessage: "Při mazání {whatError} nastala chyba!",
          },
          { whatError }
        ),
        10
      );
      handleError(err);
    });
}

/**
 * načte entitu pomocí HTTP GET
 * @param entity název entity
 * @param entityId ID entity
 * @param setLoading funkce pro nastavení načítání
 * @param setEntity funkce pro nastavení entity
 * @param parameters parametry pro query string
 */
export function getEntity(
  entity: string,
  entityId: number,
  setLoading: any,
  setEntity: any,
  parameters?: any
): void {
  setLoading && setLoading(true);
  const query = `${entity}${entityId ? `/${entityId}` : ""}${createQueryString(
    parameters
  )}`;
  get(query)
    .then((r) => {
      if (checkHttpStatus(r) && checkResponseGet(r)) {
        setEntity(r.data.data);
      } else {
        showMessageIfExists(r);
        setEntity("e");
      }
      setLoading && setLoading(false);
    })
    .catch((err) => {
      setEntity("e");
      setLoading && setLoading(false);
      handleError(err);
    });
}

/**
 * načte seznam entit pomocí HTTP GET
 * @param entity název entity
 * @param setEntities funkce pro nastavení entit
 * @param mapFunction mapovací funkce - nepovinná, pokud není nechá data v originále
 * @param parameters parametry pro query string
 * @param cleanData jestli ma dojit k promazani dat pomoci setEntities
 */
export function getEntities(
  entity: string,
  setEntities: any,
  mapFunction?: any,
  parameters?: any,
  cleanData = true,
  timeout?: number
): void {
  cleanData && setEntities();
  const query = entity + createQueryString(parameters);
  get(query, true, timeout)
    .then((r) => {
      if (checkHttpStatus(r) && checkResponseGetMany(r)) {
        setEntities(
          mapFunction ? r.data.values.map(mapFunction) : r.data.values
        );
      } else {
        showMessageIfExists(r);
        setEntities("e");
      }
    })
    .catch((err) => {
      setEntities("e");
      handleError(err);
    });
}

/**
 * ukáže odpověď API, pokud existuje
 * @param response odpověď od axios
 */
export function showMessageIfExists(response: AxiosResponse): void {
  response &&
    response.data &&
    response.data.message &&
    error(response.data.message, 10);
}

/**
 * vytvoří query string z parametrů
 * @param parameters parametry
 */
export function createQueryString(parameters?: any): string {
  return parameters
    ? `?${Object.keys(parameters)
        .filter(
          (key) => parameters[key] !== undefined && parameters[key] !== null
        )
        .map((key) => `${key}=${encodeURIComponent(parameters[key])}`)
        .join("&")}`
    : "";
}

/**
 * vytvoří model pro PATCH
 * @param properties parametry pro PATCH
 */
function createPatchModel(properties: any): Record<string, unknown> {
  return (
    properties &&
    Object.keys(properties).map((key) => ({
      propertyName: key,
      value: properties[key],
    }))
  );
}
